<script>
	import thumb_qxM4_xEanY0 from '$lib/Components/Youtube/images/qxM4_xEanY0.jpg?w=610';

	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Parallax from '$lib/Components/Parallax/Parallax.svelte';
	import Youtube from '$lib/Components/Youtube/Youtube.svelte';
	import Awards from '$lib/PageSections/Awards/Awards.svelte';
	import CommercialForm from '$lib/PageSections/CommercialForm/CommercialForm.svelte';
	import FeaturedArticles from '$lib/PageSections/FeaturedArticles/FeaturedArticles.svelte';
	import OurLocation from '$lib/PageSections/OurLocation/OurLocation.svelte';

	import hugeIndowWindowInsertImage from './images/about-us-careers-bottom.png?w=1200;1000;800;600;400;';
	import federalHistoricBuilding from './images/federal-historic-building.png';
	import windowsInsert from './images/historic-house-preservation.png';
	import logos from './images/logos.png';
</script>

<!-- eslint-disable better-tailwindcss/no-unknown-classes -->

<Section backgroundColor="white">
	<p>
		Undertaking a historic building renovation? Building owners and developers
		applying for a Federal Rehabilitation Tax Credit – also known as the
		Historic Preservation Tax Credit – are urged to retrofit, rebuild and
		restore historic windows instead of replace them. They can use Indow inserts
		to make those original windows perform like high-performance double-panes.
	</p>
	<p>
		Get a tax credit on windows by using window inserts during your historic
		building renovation.
	</p>
	<p>
		Read more about
		<a
			href="https://www.nps.gov/orgs/1739/index.htm"
			rel="external noreferrer"
			target="_blank"
		>
			The Secretary of the Interior's Standards for the Treatment of Historic
			Properties.
		</a>
	</p>
	<p>
		Read more about the
		<a
			href="https://www.nps.gov/subjects/taxincentives/index.htm"
			target="_blank"
			rel="noreferrer"
		>
			Historic Preservation Tax Credit.
		</a>
	</p>
	<Image
		src={federalHistoricBuilding}
		alt="The outside view of a large building"
	/>
</Section>

<Section backgroundColor="white">
	<h2>How do Federal Historic Tax Credits Work?</h2>
	<p>
		Historic building renovations are appealing simply because they help retain
		materials and a direct tie to our history. But, our tax code provides
		another appealing reason: historic tax credits. These tax credits reward
		those who preserve rather than replace older buildings. If you want a full
		explanation go here: <a
			href="https://www.nps.gov/tps/tax-incentives.htm"
			target="_blank"
			rel="noreferrer">How do historic tax credits work</a
		>.
	</p>
	<p>
		For a tax credit on windows, either residential or commercial can apply, but
		you must file the paperwork associated with becoming a certified historic
		structure. Then you simply renovate the windows and install Indow window
		inserts to add energy efficiency and credit your windows.
	</p>
	<Image src={windowsInsert} alt="A beautiful view of a home garden." />
</Section>

<Section backgroundColor="white">
	<h2>Historic Preservation Through Window Inserts</h2>
	<p>
		Saving energy and adding energy efficiency doesn't require replacing
		historic windows. A US Department of Energy study found Indow window inserts
		saved 20% in heating and cooling costs in a century-old Seattle home. In
		this way, window inserts help drafty single-pane windows perform like new
		double-panes.
	</p>
	<p>
		Historic building renovations can also bring modern comforts like noise
		reduction. Older windows not only let in drafts, they let in noise since <a
			href="/resources/soundproofing-windows">sound vibrations</a
		>
		move anywhere air does. This can be a particular problem in historic commercial
		districts with pedestrian and vehicle traffic. Indow window inserts can
		<a href="/solutions/window-noise-reduction">reduce outside noise</a> by up to
		70%* without changing the appearance of the window.
	</p>
</Section>

<Section backgroundColor="white">
	<h2>Window Technology for Sustainable Buildings</h2>
	<p>
		It's an important sustainability goal for many companies to increase the
		energy efficiency of their built environment while retaining as many
		existing materials as possible. Old-growth wood especially has significant
		amounts of embodied energy which Indow inserts allow to stay in place and
		out of landfill.
	</p>
	<p>
		Historic building renovation helps residential property managers as well as
		commercial. Renters are attracted to the hustle and bustle of historic
		downtowns, but want peace and quiet inside. Apartment property owners can
		install Indow inserts to meet historic guidelines and keep tenants happy.
		They won't be sad about the lower energy bills either.
	</p>
	<Youtube posterImageSrc={thumb_qxM4_xEanY0} videoId="qxM4_xEanY0" />
	<p class="disclaimer">
		*We want to ensure our inserts help solve your noise problem. Overall noise
		reduction depends on the amount of noise coming through your walls,
		ceilings, floors, doors, and existing windows. Noise reduction will be less
		when placed over double-pane windows. Read our full <a
			href="/noise-disclaimer">Noise Reduction Sheet</a
		> to learn more.
	</p>
	<Bubble
		title="Indow Inserts are Perfect for Historic Building Renovation Because They are:"
	>
		<ul>
			<li>Safe for historic windows - no mounting hardware required.</li>
			<li>Increase energy efficiency significantly.</li>
			<li>Block up to 70% of outside noise.</li>
			<li>
				Blend into the surrounding frame & aren't visible from the outside.
			</li>
			<li>Provides superior comfort.</li>
		</ul>
	</Bubble>
	<p>
		Indow is a recognized leader in energy efficiency, sound, and comfort in the
		built environment. We offer courses for AIA accreditation providing
		attendees with 1 LU/HSW credit. Join those from backgrounds in adaptive
		reuse, multifamily, historic buildings on the national register, and more.
	</p>
	<Image src={logos} alt="Company logos." />
	<ul class="chart">
		<li>
			<div></div>
			<div class="circle-outer bg-indow-blue">
				<h3>INDOW</h3>
			</div>
			<div class="circle-outer bg-indow-grey-dark">
				<h3 class="small">REPLACEMENT<br />WINDOWS</h3>
			</div>
		</li>
		<li>
			<p class="justify-self-end text-end">Alters Architectural Integrity</p>
			<p class="text-indow-blue!">NO</p>
			<p>YES</p>
		</li>
		<hr class="col-span-3" />
		<li>
			<p class="justify-self-end text-end">Disrupts Home Life</p>
			<p class="text-indow-blue!">NO</p>
			<p>YES</p>
		</li>
		<hr class="col-span-3" />
		<li>
			<p class="justify-self-end text-end">Possible Lead Paint Issues</p>
			<p class="text-indow-blue!">NO</p>
			<p>YES</p>
		</li>
	</ul>
</Section>

<Section backgroundColor="white">
	<p>
		Indow is committed to historic preservation and increasing energy efficiency
		in older homes. Our inserts are powerful tools for those involved in
		historic property management. We have a <a
			href="/commercial-storm-windows/pilot-program">pilot program</a
		> to test our window inserts in one room of your property. To see if they're available
		for your historic space, enter your zip code in the form below.
	</p>
</Section>
<CommercialForm id="free-consultation" />
<Parallax image={hugeIndowWindowInsertImage} />
<OurLocation />
<Awards />
<FeaturedArticles />

<style>
	.chart {
		display: grid;
		grid-template-columns: 1fr 10ch 10ch;
		row-gap: 1rem;
		justify-items: center;
		font-size: clamp(1rem, 3vw, 1.5rem);
		text-transform: uppercase;
		column-gap: min(1rem, 1vw);
	}
	.chart li {
		display: contents;
	}
	.chart p {
		line-height: 1;
		padding: 0;
	}
	.chart h3 {
		padding: 0.5rem;
	}
	.chart hr {
		border-color: var(--color-indow-grey-dark);
	}
	.chart .circle-outer {
		aspect-ratio: 1 /1;
		clip-path: circle(49%);
		display: grid;
		place-content: center;
	}
	.chart .small {
		font-size: clamp(0.7rem, 2vw, 1rem);
	}
</style>
